//@flow
/**
 * Collection of learner utility functions
 * @module learner/lib
 */
import qs from 'qs'
import uuid from 'uuid'
import { TextEncoder } from 'text-encoding'
import { encode as encodeToBase64 } from 'base64-arraybuffer'
import { sleep } from '../../utils'
import socket from '../../socket'
import { LearnerRecord } from './reducer'
import { publicKey, allTestLevels } from '../../constants'

/**
 * Sends a question submission to the server.
 * @memberof learner/lib
 */
export function submitQuestion (submission: QuestionSubmission) {
  return new Promise((resolve, reject) => {
    socket.emit('learner:submit-question', submission, (err, response) => {
      if (err) {
        return reject(err)
      }
      resolve(response)
    })
  })
}

/**
 * Sends a learner form submission to the server.
 * @memberof learner/lib
 */
export function submitLearner (submission: LearnerDetailsSubmission) {
  return new Promise((resolve, reject) => {
    socket.emit('learner:details', submission, (err, response) => {
      if (err) {
        console.log('submitLearner error', err)
        return reject(err)
      }
      resolve(response)
    })
  })
}

/**
 * Adds invite to browser location if PushState api available.
 * @memberof learner/lib
 */
export function addInviteToLocation (invite: string) {
  if (window.history.pushState) {
    const searchString = qs.stringify({
        ...qs.parse(window.location.search, {
          ignoreQueryPrefix: true
        }),
        invite
      }),
      path = `${window.location.pathname}?${searchString}`
    window.history.pushState(
      {
        path
      },
      '',
      path
    )
  }
}

/**
 * return a promise that resolves when the learner is registered.
 * @memberof learner/lib
 */
export async function whenLearnerRegistered (pollFreq: number = 1000) {
  let registered = false
  while (true) {
    registered = await new Promise(resolve => {
      socket.emit('status', async status => {
        resolve(status && status.registered)
      })
    })
    if (registered) return
    await sleep(pollFreq)
  }
}

/**
 * returns the proposed quiz id based on url query string ?quizId=...
 * @memberof learner/lib
 */
export function getProposedQuizId () {
  const { quizId: proposedQuizId, invite } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true
  })
  if (!allTestLevels.includes(proposedQuizId) && !invite) {
    return redirectToShortQuiz('', invite);
  }
  redirectToShortQuiz(proposedQuizId, invite);
  return proposedQuizId
}

/**
 * encrypts all pending submissions, form, and questions.
 * @memberof learner/lib
 */
export async function encryptPendingSubmissions (learnerState: LearnerRecord) {
  if (window.crypto.subtle == null) {
    throw new Error('Webcrypto unavailable.')
  }
  // Construct submissions object
  const form = learnerState.pendingFormSubmission
    ? learnerState.pendingFormSubmission.toJS()
    : null
  const invite = learnerState.invite !== '' ? learnerState.invite : null
  const submissions = {
    // only provide form if invite is not provided
    form: invite == null ? form : null,
    invite,
    answers: learnerState.pendingQuestionSubmissions
      .toJS()
      .map(({ answer }) => ({ ...answer, correct: undefined }))
  }
  if (submissions.form == null && submissions.invite == null) {
    throw new Error(
      'Unable to identify learner. No form submission or invite code provided.'
    )
  }
  const data = stringToArrayBuffer(JSON.stringify(submissions))
  const key = await generateKey()
  const { data: encryptedData, iv } = await encryptData(key, data)
  const encryptedKey = await encryptKey(key)
  const encryptedIv = await encryptPublic(iv)
  const id = uuid.v4()
  const encryptedId = await encryptPublic(stringToArrayBuffer(id))

  return {
    encryptedFileId: id,
    encryptedFile: JSON.stringify({
      id: encryptedId,
      key: encryptedKey,
      iv: encryptedIv,
      data: encryptedData
    })
  }
}

/**
 * wrapper for calls to window.crypto.subtle to handle non-standard ie11 implementation
 * @memberof learner/lib
 */
function subtleToPromise (maybePromise) {
  if (maybePromise != null && typeof maybePromise.then === 'function') {
    return maybePromise
  } else {
    return new Promise((resolve, reject) => {
      const cryptoOperation = maybePromise
      cryptoOperation.oncomplete(resolve)
      cryptoOperation.onerror(reject)
      cryptoOperation.onabort(() =>
        reject(new Error('CryptoOperation aborted.'))
      )
    })
  }
}

/**
 * Encrypts generated key with public key
 * @memberof learner/lib
 */
async function encryptKey (key) {
  const exportedKey = await subtleToPromise(
    window.crypto.subtle.exportKey('jwk', key)
  )
  return encryptPublic(stringToArrayBuffer(JSON.stringify(exportedKey)))
}

/**
 * Encrypts data with public key
 * @memberof learner/lib
 */
async function encryptPublic (data) {
  const importedKey = await getPublicKey()
  const encrypted = await subtleToPromise(
    window.crypto.subtle.encrypt('RSA-OAEP', importedKey, data)
  )
  return encodeToBase64(encrypted)
}

/**
 * Get the public encryption key.
 * @memberof learner/lib
 */
function getPublicKey () {
  return subtleToPromise(
    window.crypto.subtle.importKey(
      'jwk',
      publicKey,
      {
        name: 'RSA-OAEP',
        hash: { name: 'SHA-512' }
      },
      false,
      ['encrypt']
    )
  )
}

/**
 * Converts a string to a ArrayBuffer
 * @memberof learner/lib
 */
function stringToArrayBuffer (str) {
  const encoder = new TextEncoder()
  return encoder.encode(str)
}

/**
 * generates key for data encryption
 * @memberof learner/lib
 */
function generateKey () {
  return subtleToPromise(
    window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 128
      },
      true,
      ['encrypt', 'decrypt']
    )
  )
}

/**
 * encrypts data
 * @memberof learner/lib
 */
async function encryptData (key, data) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const encryptedData = await subtleToPromise(
    window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      data
    )
  )
  return { data: encodeToBase64(encryptedData), iv }
}

/**
 * Sends a question submission to the server.
 * @memberof learner/lib
 */
export function submitDlrqResult (
  result: DlrqResult,
  responses: Array<DlrqResponse>
) {
  return new Promise((resolve, reject) => {
    socket.emit(
      'learner:submit-dlrq-results',
      { result, responses },
      (err, response) => {
        if (err) {
          console.log('socker error', err)
          return reject(err)
        }
        resolve(response)
      }
    )
  })
}

/**
 * redirects to new short quiz ui
 * @memberof learner/lib
 */
export function redirectToShortQuiz (testLevel, invite = null) {
  if (testLevel === '' || testLevel === 'acsf3shortcourse' || testLevel === 'acsf3' || testLevel === 'acsf4' || testLevel === 'vfh' || (!testLevel && !invite)) {
    let deployment = window.location.hostname.split('.')[0];
      const inviteParam = invite !== null ? `?invite=${invite}` : `${testLevel !== '' ? `?quizId=${testLevel}` : ''}`
    let url = `http://localhost:3000/${inviteParam}`
    if (deployment !== 'localhost') {
      url = `https://${deployment}.lln.training/quiz/${inviteParam}`;
    }
    window.location.href = url;
  }
}



// WEBPACK FOOTER //
// ./src/state/learner/lib.js